import { Category, getStaticCategory } from '@/api/models';
import { AdvancedTable } from '@/components/ui/AdvancedTable';
import { Button } from '@/components/ui/Button';
import { CardBox } from '@/components/ui/CardBox';
import { mdiDelete, mdiEye, mdiPencil } from '@mdi/js';
import * as React from 'react';

export interface CategoriesTableProps {
  categories: Category[];
  isLoading: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (key: string) => void;
  onRowClick: (category: Category) => void;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (category: Category) => void;
  isDeleting: boolean;
}

export const CategoriesTable: React.FC<CategoriesTableProps> = ({
  categories,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  onEditCategory,
  onDeleteCategory,
  isDeleting,
}) => {
  const columns = [
    { key: 'emoji', label: '', sortable: false, className: 'w-12' },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'status', label: 'Status', sortable: false, className: 'w-24' },
    {
      key: 'businessCount',
      label: 'Businesses',
      sortable: false,
      className: 'w-28',
    },
    { key: 'createdAt', label: 'Created', sortable: true, className: 'w-32' },
  ];

  const renderCell = (
    key: string,
    value: string | number | boolean | string[] | undefined,
    item: Category,
  ): React.ReactNode => {
    const staticCategory = getStaticCategory(item.name);

    switch (key) {
      case 'emoji':
        return (
          <span className='text-2xl'>{staticCategory?.emoji || '📁'}</span>
        );
      case 'name':
        return (
          <div>
            <div className='font-medium text-gray-900'>
              {staticCategory?.label || item.name}
            </div>
            <div className='text-sm text-gray-500'>{item.name}</div>
          </div>
        );
      case 'status':
        return item.enabled ? (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
            Enabled
          </span>
        ) : (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
            Disabled
          </span>
        );
      case 'businessCount':
        return (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700'>
            {item.businessCount ?? 0}
          </span>
        );
      case 'createdAt':
        return (
          <div className='text-sm text-gray-900'>
            {item.createdAt
              ? new Date(item.createdAt).toLocaleDateString()
              : 'N/A'}
          </div>
        );
      default:
        return String(value || '-');
    }
  };

  const renderActions = (category: Category) => (
    <div className='flex items-center space-x-2'>
      <Button
        icon={mdiEye}
        small
        color='info'
        title='View Details'
        onClick={() => onRowClick(category)}
      />
      <Button
        icon={mdiPencil}
        small
        color='warning'
        title='Edit Category'
        onClick={() => onEditCategory(category)}
      />
      <Button
        icon={mdiDelete}
        small
        color='danger'
        title='Delete Category'
        loading={isDeleting}
        onClick={() => onDeleteCategory(category)}
      />
    </div>
  );

  return (
    <CardBox hasTable>
      <AdvancedTable
        columns={columns}
        data={categories}
        loading={isLoading}
        onSort={onSort}
        sortKey={sortBy}
        sortDirection={sortOrder}
        renderCell={renderCell}
        renderActions={renderActions}
        onRowClick={onRowClick}
        emptyMessage='No categories found. Try adjusting your search criteria.'
      />
    </CardBox>
  );
};
